import DOMPurify from 'dompurify';
import FormattingTools from '../../components/FormattingTools';
import '../../styles/milestone.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';

/* ─── Rich-text editor (page body text) ───
 * contentEditable wrapper + toolbar (font, size, B/I/U, colour). Stores
 * sanitized HTML in htmlEn/htmlAr; the server derives the plain-text
 * shadow into textEn/textAr so the audio aligner is unaffected.
 *
 * execCommand is officially deprecated but still implemented across all
 * current browsers; for an admin-only tool the simplicity wins. We set
 * styleWithCSS so bold/italic/underline emit <span style="..."> instead
 * of legacy <b>/<i>/<u>, which round-trips cleanly through the sanitizer.
 */
const FONT_FAMILIES = [
  { label: 'Default', value: '' },
  { label: 'Garamond', value: 'Garamond, "EB Garamond", serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace' },
];
const FONT_SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 56, 64, 72, 84, 96];

function RichTextEditor({ value, onChange, dir = 'ltr', minHeight = 140, slug }) {
  const ref = useRef(null);
  // Hidden <input type="file"> the image buttons trigger. The chosen
  // alignment (left/center/right) is stashed in imgAlignRef so the
  // single onChange handler knows which class to apply on insert.
  const imgPickerRef = useRef(null);
  const imgAlignRef = useRef('center');
  const [imgUploading, setImgUploading] = useState(false);
  const [imageCaption, setImageCaption] = useState('');
  // Last <img> the user clicked inside the editor. The toolbar's Img×
  // button targets this element; pressing Delete/Backspace also removes
  // it (via the native range selection we set on click).
  const selectedImageRef = useRef(null);
  // Sentinel null so the very first sync always writes — the value→DOM
  // sync skips writes that match what we *just* emitted (echo from the
  // parent's controlled re-render) but must populate the editor on mount.
  const lastEmittedRef = useRef(null);
  // Remember the user's last selection range inside the editor so toolbar
  // controls (especially <select> dropdowns) can restore it after focus
  // moves to the control. Without this, clicking any toolbar widget
  // collapses the selection and B/I/U/colour/font/size apply to nothing.
  const savedRangeRef = useRef(null);
  const selectedAnnotationRef = useRef(null);
  const [color, setColor] = useState('#000000');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = DOMPurify.sanitize(value || '');
    if (incoming !== lastEmittedRef.current) {
      el.innerHTML = incoming;
      lastEmittedRef.current = incoming;
    }
  }, [value]);

  const captureSelection = () => {
    const el = ref.current;
    if (!el) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const r = sel.getRangeAt(0);
    if (el.contains(r.commonAncestorContainer)) {
      savedRangeRef.current = r.cloneRange();
    }
  };

  const restoreSelection = () => {
    const r = savedRangeRef.current;
    if (!r) return false;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(r);
    return true;
  };

  const exec = (cmd, arg = null) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    restoreSelection();
    const alignment = { justifyLeft: 'left', justifyCenter: 'center', justifyRight: 'right', justifyFull: 'justify' }[cmd];
    if (alignment) {
      const node = window.getSelection()?.anchorNode;
      const selected = selectedAnnotationRef.current;
      const annotation = (selected && el.contains(selected) && !selected.classList.contains('rte-arrow') ? selected : null)
        || (node?.nodeType === 1 ? node : node?.parentElement)?.closest('.rte-annotation:not(.rte-arrow)');
      if (annotation && el.contains(annotation)) {
        annotation.style.textAlign = alignment;
        captureSelection();
        emit();
        return;
      }
    }
    try { document.execCommand('styleWithCSS', false, true); } catch { /* ignore */ }
    try { document.execCommand(cmd, false, arg); } catch { /* ignore */ }
    captureSelection();
    emit();
  };

  // Wrap the current selection in a <span> with the given inline styles.
  // Used for font-size + font-family because execCommand('fontSize') only
  // supports legacy 1–7 buckets and ('fontName') emits <font> tags that
  // round-trip badly.
  const wrapSelection = (styleObj) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    restoreSelection();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed) return;
    const span = document.createElement('span');
    Object.entries(styleObj).forEach(([k, v]) => { span.style[k] = v; });
    try {
      range.surroundContents(span);
    } catch {
      // surroundContents throws if the range partially crosses an
      // element boundary — fall back to extract + append, which always
      // works but may flatten some nested structure.
      const contents = range.extractContents();
      span.appendChild(contents);
      range.insertNode(span);
    }
    sel.removeAllRanges();
    const r2 = document.createRange();
    r2.selectNodeContents(span);
    sel.addRange(r2);
    captureSelection();
    emit();
  };

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    // Selection outline belongs to the editor UI, never to saved book HTML.
    const copy = el.cloneNode(true);
    copy.querySelectorAll('.rte-annotation-selected').forEach(node => node.classList.remove('rte-annotation-selected'));
    const html = copy.innerHTML;
    lastEmittedRef.current = html;
    onChange?.(html);
  };

  const onFontFamily = (e) => {
    const v = e.target.value;
    if (!v) return;
    wrapSelection({ fontFamily: v });
    e.target.value = '';
  };
  const onFontSize = (e) => {
    const v = e.target.value;
    if (!v) return;
    wrapSelection({ fontSize: `${v}px` });
    e.target.value = '';
  };
  const onColor = (e) => {
    const v = e.target.value;
    setColor(v);
    exec('foreColor', v);
  };

  // preventDefault on mousedown stops the browser from moving focus out
  // of the contentEditable when the button is clicked — without this the
  // selection collapses and B/I/U/colour/font/size apply to nothing.
  const swallowMouseDown = (e) => e.preventDefault();
  const btn = (label, title, onClick, extra = {}) => (
    <button
      type="button"
      title={title}
      onMouseDown={swallowMouseDown}
      onClick={onClick}
      style={{
        minWidth: 32, height: 30, padding: '0 8px',
        background: 'rgba(184,134,11,0.12)',
        border: '1px solid rgba(184,134,11,0.4)',
        color: 'var(--admin-fg)', borderRadius: 6,
        cursor: 'pointer', fontWeight: 700,
        ...extra,
      }}
    >{label}</button>
  );
  const selectStyle = {
    height: 30, padding: '0 8px',
    background: 'rgba(184,134,11,0.08)',
    border: '1px solid rgba(184,134,11,0.4)',
    color: 'var(--admin-fg)', borderRadius: 6,
    fontSize: '0.78rem',
  };

  // Per-paragraph spacing — no inter-block margin. Enter emits a <p>
  // (defaultParagraphSeparator), which itself takes a full line by
  // virtue of being a block element; that's the natural "one line"
  // separation, and an empty paragraph (Enter twice) is one extra line.
  // Reader uses the identical rule so what you see here is what the
  // reader renders. The editor previews of float/centre image classes
  // mirror the reader CSS so admin sees what the reader will show.
  const editorParagraphCss = `
    .rte-editor p, .rte-editor div { margin: 0; }
    .rte-editor:empty::before { content: ""; }
    .rte-editor img { max-width: 100%; height: auto; border-radius: 14px; border: 3px solid rgba(184,134,11,0.5); box-shadow: 0 0 12px rgba(20,10,0,0.55); object-fit: contain; }
    /* All three variants put the image on its own line — only the
       horizontal alignment changes. No floats, so text never wraps
       around the image; it sits above and below cleanly. */
    .rte-editor img.rte-img-left   { display: block; max-width: 60%; max-height: 260px; margin: 0.5em auto 0.5em 0; }
    .rte-editor img.rte-img-center { display: block; max-width: 70%; max-height: 300px; margin: 0.6em auto; }
    .rte-editor img.rte-img-right  { display: block; max-width: 60%; max-height: 260px; margin: 0.5em 0 0.5em auto; }
    /* Wrap variants — image floats and surrounding text flows around
       it. Smaller default max-width than the block variants so the
       text column beside has room to breathe. */
    .rte-editor img.rte-img-wrap-left  { float: left;  max-width: 40%; max-height: 240px; margin: 0.3em 1em 0.5em 0; }
    .rte-editor img.rte-img-wrap-right { float: right; max-width: 40%; max-height: 240px; margin: 0.3em 0 0.5em 1em; }
    .rte-editor .rte-img-caption { display: block; clear: both; max-width: 100%; margin: 0.15em auto 0.65em; color: var(--admin-fg-muted); font-size: 0.82em; line-height: 1.45; text-align: center; overflow-wrap: anywhere; }
    /* Clearfix on the editor surface so a wrap-image at the end of
       the content doesn't escape its container. */
    .rte-editor::after { content: ''; display: table; clear: both; }
    /* Visual effects — combine with alignment classes. The selectors
       are duplicated in reader.css so what you see here = what the
       reader shows. */
    .rte-editor img.rte-img-fx-glow {
      box-shadow: 0 0 28px rgba(240, 197, 96, 0.55), 0 0 12px rgba(20, 10, 0, 0.4);
    }
    .rte-editor img.rte-img-fx-shadow {
      box-shadow: 0 18px 36px rgba(0, 0, 0, 0.55), 0 6px 14px rgba(0, 0, 0, 0.4);
      border-color: rgba(184, 134, 11, 0.7);
    }
    .rte-editor img.rte-img-fx-halo {
      box-shadow: 0 0 0 4px rgba(240, 197, 96, 0.55), 0 0 24px rgba(240, 197, 96, 0.55), 0 0 12px rgba(20, 10, 0, 0.4);
      border-color: rgba(255, 220, 130, 0.85);
    }
    /* Plain — strip the gold border + drop shadow for a bare image. */
    .rte-editor img.rte-img-fx-plain {
      border: 0;
      box-shadow: none;
    }
    /* Firefly brushstroke — mirrors the home-page showcase rows:
       coloured blob behind the image, three glowing dots, image
       tilted with a hover lift. Three colour options (green / orange
       / purple). The image fills 100% of the wrap; the blob extends
       past the wrap edges via negative inset. */
    .rte-editor .rte-img-firefly-wrap {
      position: relative;
      display: block;          /* block so margin auto centres it */
      margin: 0.6em auto;       /* default = centre */
    }
    .rte-editor .rte-img-firefly-wrap.rte-img-left   { margin: 0.5em auto 0.5em 0; }
    .rte-editor .rte-img-firefly-wrap.rte-img-center { margin: 0.6em auto; }
    .rte-editor .rte-img-firefly-wrap.rte-img-right  { margin: 0.5em 0 0.5em auto; }
    .rte-editor .rte-img-firefly-wrap > img {
      position: relative;
      z-index: 2;
      display: block !important;
      width: 100% !important;
      max-width: 100% !important;
      height: auto !important;
      max-height: none !important;
      margin: 0 !important;
      border: 0 !important;     /* no gold frame on firefly images */
      border-radius: 14px;
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.28);
      transform: rotate(-1.5deg);
      transition: transform 0.6s cubic-bezier(.2, .8, .25, 1), box-shadow 0.4s ease;
    }
    /* Hover — straighten + lift + slight zoom, same as showcase rows. */
    .rte-editor .rte-img-firefly-wrap:hover > img {
      transform: rotate(0) translateY(-3px) scale(1.04);
      box-shadow: 0 22px 40px rgba(0, 0, 0, 0.4);
    }
    .rte-editor .rte-img-firefly-wrap > .rte-img-firefly-blob,
    .rte-editor .rte-img-firefly-wrap::before {
      content: '';
      position: absolute;
      inset: -8% -10%;
      z-index: 0;
      border-radius: 32% 68% 47% 53% / 38% 56% 44% 62%;
      filter: drop-shadow(0 12px 28px rgba(0, 0, 0, 0.2));
      pointer-events: none;
      background: #3fb088;
    }
    .rte-editor .rte-img-firefly-wrap.rte-img-firefly-green  > .rte-img-firefly-blob,
    .rte-editor .rte-img-firefly-wrap.rte-img-firefly-green::before  { background: #3fb088; }
    .rte-editor .rte-img-firefly-wrap.rte-img-firefly-orange > .rte-img-firefly-blob,
    .rte-editor .rte-img-firefly-wrap.rte-img-firefly-orange::before { background: #e76f51; }
    .rte-editor .rte-img-firefly-wrap.rte-img-firefly-purple > .rte-img-firefly-blob,
    .rte-editor .rte-img-firefly-wrap.rte-img-firefly-purple::before { background: #9466c4; }
    /* Left/right aligned — pin the blob to the outer edge so it
       doesn't extend past the page column and get clipped. Keeps the
       inward overhang for the same visual bleed toward the text. */
    .rte-editor .rte-img-firefly-wrap.rte-img-left  > .rte-img-firefly-blob,
    .rte-editor .rte-img-firefly-wrap.rte-img-left::before  { left:  0; }
    .rte-editor .rte-img-firefly-wrap.rte-img-right > .rte-img-firefly-blob,
    .rte-editor .rte-img-firefly-wrap.rte-img-right::before { right: 0; }
    /* Three glowing dots — exact match for .showcase-dot in
       gallery.css. Children order in the wrap: 1 = blob, 2 = img,
       3-5 = sparks. */
    .rte-editor .rte-img-firefly-wrap > .rte-img-firefly-spark {
      position: absolute;
      z-index: 3;
      border-radius: 50%;
      background: radial-gradient(circle, #fff 0%, #ffe9a8 50%, transparent 75%);
      box-shadow: 0 0 12px rgba(255, 233, 168, 0.85);
      pointer-events: none;
      animation: rte-firefly-float 4.5s ease-in-out infinite;
      opacity: 0.85;
    }
    .rte-editor .rte-img-firefly-wrap > .rte-img-firefly-spark:nth-child(3) { width: 12px; height: 12px; top: 6%;   right: 4%;  animation-delay: 0s;   }
    .rte-editor .rte-img-firefly-wrap > .rte-img-firefly-spark:nth-child(4) { width: 8px;  height: 8px;  bottom: 12%; left: 2%;  animation-delay: 1.2s; }
    .rte-editor .rte-img-firefly-wrap > .rte-img-firefly-spark:nth-child(5) { width: 10px; height: 10px; top: 38%;  left: -1%;  animation-delay: 2.1s; }
    .rte-editor .rte-img-firefly-wrap > .rte-img-firefly-spark:nth-child(n+6) { display: none; }
    @keyframes rte-firefly-float {
      0%, 100% { transform: translateY(0)    scale(1);    opacity: 0.85; }
      50%      { transform: translateY(-8px) scale(1.12); opacity: 1;    }
    }
  `;

  const insertImageAtCursor = (url, align) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    restoreSelection();
    const sel = window.getSelection();
    let range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
    if (!range || !el.contains(range.commonAncestorContainer)) {
      range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false); // end of editor
    }
    range.deleteContents();

    const img = document.createElement('img');
    img.src = url;
    img.alt = '';
    img.draggable = false;
    img.className =
      align === 'wrap-left'  ? 'rte-img-wrap-left'  :
      align === 'wrap-right' ? 'rte-img-wrap-right' :
      align === 'left'       ? 'rte-img-left'       :
      align === 'right'      ? 'rte-img-right'      :
                               'rte-img-center';
    range.insertNode(img);
    // For wrap-left / wrap-right floats: drop an inline text-node
    // sibling after the image and park the caret inside it. Without
    // this anchor, contenteditable wraps the user's keystrokes in a
    // fresh <div> sibling of the image — and that block sits BELOW
    // the float instead of flowing beside it (the user sees their
    // text drop under the image instead of wrapping). A zero-width
    // space text node gives the caret a real inline home next to
    // the float so typed characters extend it inline and wrap.
    if (align === 'wrap-left' || align === 'wrap-right') {
      const tail = document.createTextNode(String.fromCharCode(0x200B));
      if (img.nextSibling) img.parentNode.insertBefore(tail, img.nextSibling);
      else img.parentNode.appendChild(tail);
      range.setStart(tail, tail.length);
      range.setEnd(tail, tail.length);
    } else {
      // Block variants — caret just after the image is fine; the
      // image is on its own line anyway.
      range.setStartAfter(img);
      range.setEndAfter(img);
    }
    sel.removeAllRanges();
    sel.addRange(range);
    captureSelection();
    emit();
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const slugRaw = slug || 'misc';
      const { data } = await axios.post(
        `/api/admin/upload/${encodeURIComponent(slugRaw)}`,
        fd,
        { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      if (data?.url) insertImageAtCursor(data.url, imgAlignRef.current || 'center');
    } catch (err) {
      alert(err?.response?.data?.message || 'Image upload failed');
    } finally {
      setImgUploading(false);
    }
  };

  const triggerImagePicker = (align) => {
    imgAlignRef.current = align;
    imgPickerRef.current?.click();
  };

  // Click an image inside the editor → select its range so the browser
  // shows the standard selection highlight (and Delete/Backspace will
  // remove it natively). Also remember it for the Img× toolbar button.
  const handleEditorClick = (e) => {
    const t = e.target;
    if (t && t.tagName === 'IMG' && ref.current && ref.current.contains(t)) {
      selectedImageRef.current = t;
      setImageCaption(getImageCaptionNode(t)?.textContent || '');
      const sel = window.getSelection();
      if (sel) {
        const r = document.createRange();
        r.selectNode(t);
        sel.removeAllRanges();
        sel.addRange(r);
        captureSelection();
      }
    } else {
      selectedImageRef.current = null;
      setImageCaption('');
    }
  };

  const getImageUnit = (img) => img?.parentNode?.classList?.contains('rte-img-firefly-wrap') ? img.parentNode : img;
  const getImageCaptionNode = (img) => {
    const next = getImageUnit(img)?.nextElementSibling;
    return next?.classList?.contains('rte-img-caption') ? next : null;
  };
  const saveImageCaption = () => {
    const img = selectedImageRef.current;
    if (!img || !ref.current?.contains(img)) return;
    const caption = imageCaption.trim().slice(0, 240);
    const existing = getImageCaptionNode(img);
    if (!caption) existing?.remove();
    else if (existing) existing.textContent = caption;
    else {
      const node = document.createElement('small');
      node.className = 'rte-img-caption';
      node.textContent = caption;
      const unit = getImageUnit(img);
      unit.parentNode.insertBefore(node, unit.nextSibling);
    }
    setImageCaption(caption);
    emit();
  };

  // If the image is wrapped in a firefly span, the whole wrap (blob +
  // sparks + img) is the unit to delete — otherwise the decorative
  // background and glowing dots are orphaned on the page.
  const deleteSelectedImage = () => {
    const img = selectedImageRef.current;
    if (!img || !ref.current || !ref.current.contains(img)) return;
    ref.current.focus();
    const target = getImageUnit(img);
    getImageCaptionNode(img)?.remove();
    if (target.parentNode) target.parentNode.removeChild(target);
    selectedImageRef.current = null;
    emit();
  };

  // Native Delete/Backspace on a selected image only removes the <img>,
  // leaving the firefly wrap (blob + sparks) behind. Intercept and
  // delete the wrap as a unit when the selected image is inside one.
  const handleEditorKeyDown = (e) => {
    if (e.key !== 'Delete' && e.key !== 'Backspace') return;
    const img = selectedImageRef.current;
    if (!img || !ref.current || !ref.current.contains(img)) return;
    e.preventDefault();
    deleteSelectedImage();
  };

  // Apply an inline size to the last-clicked image. Sets both width and
  // max-width so the resize wins over the class-level max-width cap
  // (which would otherwise clamp e.g. a float to 38%). max-height
  // becomes 'none' so the image can grow vertically with width.
  const resizeSelectedImage = (pct) => {
    const img = selectedImageRef.current;
    if (!img || !ref.current || !ref.current.contains(img)) {
      alert('Click on an image in the editor first, then pick a size.');
      return;
    }
    ref.current.focus();
    // If the image is inside a firefly wrap, the WRAP holds the width
    // (so it can hug the image). Resize the wrap; the image fills it
    // via CSS. Otherwise resize the image directly.
    const parent = img.parentNode;
    const target = parent && parent.classList && parent.classList.contains('rte-img-firefly-wrap')
      ? parent
      : img;
    if (!pct) {
      target.style.width = '';
      target.style.maxWidth = '';
      target.style.maxHeight = '';
      target.style.height = '';
    } else {
      target.style.width = `${pct}%`;
      target.style.maxWidth = `${pct}%`;
      target.style.maxHeight = 'none';
      target.style.height = 'auto';
    }
    emit();
  };

  // Firefly variants — modelled on the home-page showcase rows:
  // colored organic blob behind the image, three glowing dots, image
  // overlaid with a slight tilt + hover-lift. Three colour options
  // matching showcase b-teal / b-coral / b-violet.
  const FIREFLY_COLORS = new Set(['green', 'orange', 'purple']);
  const SPARK_COUNT = 3;
  // Pull rte-img-left / -center / -right off the img (or off whatever
   // image classes look like) so it can be applied to the wrapper.
   // Without this the alignment class sits on the img inside the wrap
   // and never positions the wrap in its paragraph.
  const pickAlignClass = (img) => {
    const cls = (img.className || '').split(/\s+/);
    return cls.find((c) => c === 'rte-img-left' || c === 'rte-img-center' || c === 'rte-img-right') || 'rte-img-center';
  };
  const ensureFireflyWrap = (img, color) => {
    const parent = img.parentNode;
    if (!parent) return;
    const COLOR_CLASS = `rte-img-firefly-${color}`;
    const alignCls = pickAlignClass(img);
    if (parent.classList && parent.classList.contains('rte-img-firefly-wrap')) {
      // Already wrapped — refresh colour + alignment classes.
      parent.classList.forEach((c) => {
        if ((c.startsWith('rte-img-firefly-') && c !== 'rte-img-firefly-wrap')
            || c === 'rte-img-left' || c === 'rte-img-center' || c === 'rte-img-right') {
          parent.classList.remove(c);
        }
      });
      parent.classList.add(COLOR_CLASS);
      parent.classList.add(alignCls);
      return;
    }
    const wrap = document.createElement('span');
    wrap.className = `rte-img-firefly-wrap ${COLOR_CLASS} ${alignCls}`;
    if (img.style.width)     { wrap.style.width    = img.style.width;    img.style.width = ''; }
    if (img.style.maxWidth)  { wrap.style.maxWidth = img.style.maxWidth; img.style.maxWidth = ''; }
    parent.insertBefore(wrap, img);
    const blob = document.createElement('span');
    blob.className = 'rte-img-firefly-blob';
    blob.setAttribute('aria-hidden', 'true');
    wrap.appendChild(blob);
    wrap.appendChild(img);
    for (let i = 0; i < SPARK_COUNT; i++) {
      const s = document.createElement('span');
      s.className = 'rte-img-firefly-spark';
      s.setAttribute('aria-hidden', 'true');
      wrap.appendChild(s);
    }
  };
  const removeFireflyWrap = (img) => {
    const parent = img.parentNode;
    if (!parent || !parent.classList || !parent.classList.contains('rte-img-firefly-wrap')) return;
    if (parent.style.width)    { img.style.width    = parent.style.width; }
    if (parent.style.maxWidth) { img.style.maxWidth = parent.style.maxWidth; }
    const grand = parent.parentNode;
    if (!grand) return;
    grand.insertBefore(img, parent);
    grand.removeChild(parent);
  };

  // Set / clear a visual effect class on the last-clicked image.
  // `fx` accepts: 'glow' | 'shadow' | 'halo' (img-class effects),
  //              'firefly-green' | 'firefly-orange' | 'firefly-purple'
  //              (wrapper-based brushstroke), or 'none' / null to clear.
  const setImageEffect = (fx) => {
    const img = selectedImageRef.current;
    if (!img || !ref.current || !ref.current.contains(img)) {
      alert('Click on an image in the editor first, then pick an effect.');
      return;
    }
    ref.current.focus();
    const filtered = (img.className || '')
      .split(/\s+/)
      .filter((c) => c && !c.startsWith('rte-img-fx-'));
    const isFirefly = typeof fx === 'string' && fx.indexOf('firefly-') === 0;
    if (isFirefly) {
      const color = fx.split('-')[1];
      if (FIREFLY_COLORS.has(color)) {
        filtered.push('rte-img-fx-firefly'); // marker on the img
        img.className = filtered.join(' ');
        ensureFireflyWrap(img, color);
        emit();
        return;
      }
    }
    if (fx && fx !== 'none') filtered.push(`rte-img-fx-${fx}`);
    img.className = filtered.join(' ');
    removeFireflyWrap(img);
    emit();
  };
  const onEditorFocus = () => {
    try { document.execCommand('defaultParagraphSeparator', false, 'p'); } catch { /* ignore */ }
  };

  return (
    <div>
      <style>{editorParagraphCss}</style>
      <FormattingTools editorRef={ref} restore={restoreSelection} capture={captureSelection} emit={emit} exec={exec} onAnnotationSelect={node => { selectedAnnotationRef.current = node; }} />
      <div style={{
        display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center',
        padding: '6px 8px', marginBottom: 6,
        background: 'var(--admin-panel-bg)',
        border: '1px solid rgba(184,134,11,0.25)',
        borderRadius: 8,
      }}>
        <select onChange={onFontFamily} defaultValue="" style={{ ...selectStyle, minWidth: 130 }} title="Font family">
          <option value="">Font…</option>
          {FONT_FAMILIES.filter((f) => f.value).map((f) => (
            <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>
          ))}
        </select>
        <select onChange={onFontSize} defaultValue="" style={{ ...selectStyle, minWidth: 70 }} title="Font size (px)">
          <option value="">Size…</option>
          {FONT_SIZES.map((s) => <option key={s} value={s}>{s}px</option>)}
        </select>
        {btn(<b>B</b>,  'Bold (Ctrl+B)',      () => exec('bold'))}
        {btn(<i>I</i>,  'Italic (Ctrl+I)',    () => exec('italic'))}
        {btn(<u>U</u>,  'Underline (Ctrl+U)', () => exec('underline'))}
        <span style={{ width: 1, height: 22, background: 'rgba(184,134,11,0.4)', margin: '0 2px' }} aria-hidden="true" />
        {btn('⇤', 'Align left',    () => exec('justifyLeft'),   { fontSize: '1rem' })}
        {btn('↔', 'Align center',  () => exec('justifyCenter'), { fontSize: '1rem' })}
        {btn('⇥', 'Align right',   () => exec('justifyRight'),  { fontSize: '1rem' })}
        {btn('≡', 'Justify',       () => exec('justifyFull'),   { fontSize: '1rem' })}
        <label title="Font colour" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 4 }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--admin-fg-muted)' }}>A</span>
          <input
            type="color"
            value={color}
            onChange={onColor}
            style={{ width: 30, height: 30, padding: 0, border: '1px solid rgba(184,134,11,0.4)', borderRadius: 6, background: 'transparent', cursor: 'pointer' }}
          />
        </label>
        {btn('Clear', 'Strip formatting from selection', () => exec('removeFormat'), { fontWeight: 500, fontSize: '0.75rem' })}
        <span style={{ width: 1, height: 22, background: 'rgba(184,134,11,0.4)', margin: '0 2px' }} aria-hidden="true" />
        {btn('Img⇤', imgUploading ? 'Uploading…' : 'Insert image — block, left-aligned',   () => triggerImagePicker('left'),   { fontWeight: 500, fontSize: '0.75rem' })}
        {btn('Img↔', imgUploading ? 'Uploading…' : 'Insert image — block, centred',        () => triggerImagePicker('center'), { fontWeight: 500, fontSize: '0.75rem' })}
        {btn('Img⇥', imgUploading ? 'Uploading…' : 'Insert image — block, right-aligned',  () => triggerImagePicker('right'),  { fontWeight: 500, fontSize: '0.75rem' })}
        {btn('Img◀', imgUploading ? 'Uploading…' : 'Insert image — wrap text on the right (float left)',  () => triggerImagePicker('wrap-left'),  { fontWeight: 500, fontSize: '0.75rem' })}
        {btn('Img▶', imgUploading ? 'Uploading…' : 'Insert image — wrap text on the left (float right)', () => triggerImagePicker('wrap-right'), { fontWeight: 500, fontSize: '0.75rem' })}
        {btn('Img×', 'Delete selected image (click an image first)', deleteSelectedImage, { fontWeight: 500, fontSize: '0.75rem' })}
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', color: 'var(--admin-fg-muted)', fontSize: '0.78rem' }}>
          Caption
          <input className="admin-input" type="text" value={imageCaption} maxLength={240} onChange={(e) => setImageCaption(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveImageCaption(); } }} placeholder="Select an image first" aria-label="Selected image caption" style={{ width: 180, minHeight: 30, padding: '3px 8px' }} />
        </label>
        {btn('Apply caption', 'Add or update the selected image caption; leave blank to remove', saveImageCaption, { fontWeight: 500, fontSize: '0.75rem' })}
        <select
          onChange={(e) => { const v = e.target.value; if (v === '') return; resizeSelectedImage(v === 'reset' ? null : Number(v)); e.target.value = ''; }}
          defaultValue=""
          style={{ ...selectStyle, minWidth: 100 }}
          title="Resize selected image (click an image first)"
        >
          <option value="">Img size…</option>
          {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((p) => (
            <option key={p} value={p}>{p}%</option>
          ))}
          <option value="reset">Reset</option>
        </select>
        <select
          onChange={(e) => { const v = e.target.value; if (v === '') return; setImageEffect(v); e.target.value = ''; }}
          defaultValue=""
          style={{ ...selectStyle, minWidth: 140 }}
          title="Visual effect on selected image (click an image first)"
        >
          <option value="">Img effect…</option>
          <option value="plain">Plain (no border)</option>
          <option value="glow">Glow</option>
          <option value="shadow">Shadow</option>
          <option value="halo">Halo</option>
          <option value="firefly-green">Firefly — green</option>
          <option value="firefly-orange">Firefly — orange</option>
          <option value="firefly-purple">Firefly — purple</option>
          <option value="none">None (clear)</option>
        </select>
        <input
          ref={imgPickerRef}
          type="file"
          accept="image/*,.jfif,.avif"
          onChange={onPickImage}
          style={{ display: 'none' }}
        />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        dir={dir}
        onFocus={onEditorFocus}
        onInput={() => { captureSelection(); emit(); }}
        onBlur={emit}
        onMouseUp={captureSelection}
        onKeyUp={captureSelection}
        onKeyDown={handleEditorKeyDown}
        onClick={handleEditorClick}
        className="admin-input rte-editor"
        style={{
          minHeight,
          padding: '10px 12px',
          whiteSpace: 'pre-wrap',
          lineHeight: 1.55,
          outline: 'none',
        }}
      />
    </div>
  );
}

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Derive an upload folder slug from an existing /uploads/books/<slug>/... URL,
// falling back to the book title.
function metaSlug(book, draft) {
  const m = (draft?.coverImage || book?.coverImage || '').match(/\/uploads\/books\/([^/]+)\//);
  if (m) return m[1];
  const t = draft?.title || book?.title || 'book';
  return t.toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'book';
}

const META_FIELDS = [
  { key: 'title',         label: 'Title (EN)',           type: 'text' },
  { key: 'titleAr',       label: 'Title (AR)',           type: 'text' },
  { key: 'author',        label: 'Author',               type: 'text' },
  { key: 'coverImage',    label: 'Cover image (EN URL)', type: 'text' },
  { key: 'coverImageAr',  label: 'Cover image (AR URL)', type: 'text' },
  { key: 'category',      label: 'Category',             type: 'text' },
  { key: 'genre',         label: 'Genre',                type: 'genre' },
  { key: 'ageRange',      label: 'Age range',            type: 'text' },
  { key: 'readingLevel',  label: 'Reading level',        type: 'select', options: ['Dawn', 'Lantern', 'Compass', 'Beacon', 'Horizon'] },
];

// Small named palette for book page backgrounds. Each entry has the
// stored enum value, a human label and a CSS colour the swatch
// previews so the admin doesn't need to know the hex.
const PAGE_BACKGROUNDS = [
  { value: 'light-blue', label: 'Light blue', css: '#e6f3fa' },
  { value: 'light-green', label: 'Light green', css: '#eaf4e5' },
  { value: 'snow',      label: 'Snow White', css: '#ffffff' },
  { value: 'white',     label: 'White',      css: '#fdfaf3' },
  { value: 'cream',     label: 'Cream',      css: '#f6e9c9' },
  { value: 'sand',      label: 'Sand',       css: '#e9d6a8' },
  { value: 'grey',      label: 'Grey',       css: '#e3e3e0' },
  { value: 'charcoal',  label: 'Charcoal',   css: '#3a3a38' },
  { value: 'midnight',  label: 'Midnight',   css: '#1e2538' },
];

const PAGE_LAYOUTS = [
  { value: 'image-text', label: 'Image + text spread', help: 'Each book page shows an illustration on the left page and text on the right.' },
  { value: 'text-text',  label: 'Text on both pages',  help: 'No illustration pages between text — left + right of every spread are story text. Insert images inline per page via the text editor.' },
];

const TABS = [
  { id: 'meta',    label: 'Details' },
  { id: 'pages',   label: 'Pages' },
  { id: 'quiz',    label: 'Quiz' },
];

export default function AdminBookEdit() {
  const { id } = useParams();
  const [book, setBook] = useState(null);
  const [tab, setTab] = useState('meta');
  const [savingMeta, setSavingMeta] = useState(false);
  const [savingPublication, setSavingPublication] = useState(false);
  const [meta, setMeta] = useState({});
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const [categories, setCategories] = useState([]);
  const [genres, setGenres] = useState([]);

  useEffect(() => {
    let alive = true;
    axios.get(`/api/admin/books/${id}`, { headers: authHeaders() })
      .then(({ data }) => {
        if (!alive) return;
        setBook(data);
        const m = {};
        META_FIELDS.forEach((f) => { m[f.key] = data[f.key] || ''; });
        m.description = data.description || '';
        m.descriptionAr = data.descriptionAr || '';
        m.hasQuiz = !!data.hasQuiz;
        m.format = data.format || 'portrait';
        m.pageBackground = data.pageBackground || 'cream';
        m.pageLayout     = data.pageLayout     || 'image-text';
        m.ratingsVisible = data.ratingsVisible !== false; // default true
        // Multi-value taxonomy: prefer the arrays, fall back to the
        // legacy singular so books saved before the change still load
        // with their single value pre-checked.
        m.categories = Array.isArray(data.categories) && data.categories.length
          ? data.categories
          : (data.category ? [data.category] : []);
        m.genres = Array.isArray(data.genres) && data.genres.length
          ? data.genres
          : (data.genre ? [data.genre] : []);
        m.tags = (data.tags || []).join(', ');
        setMeta(m);
      })
      .catch((e) => alive && setErr(e?.response?.data?.message || 'Failed to load'));
    axios.get('/api/admin/categories', { headers: authHeaders() })
      .then(({ data }) => alive && setCategories(Array.isArray(data) ? data : []))
      .catch(() => alive && setCategories([]));
    axios.get('/api/admin/genres', { headers: authHeaders() })
      .then(({ data }) => alive && setGenres(Array.isArray(data) ? data : []))
      .catch(() => alive && setGenres([]));
    return () => { alive = false; };
  }, [id]);

  const ping = (msg) => { setToast(msg); setTimeout(() => setToast(''), 1800); };

  const saveMeta = async () => {
    setSavingMeta(true);
    setErr('');
    try {
      const { data } = await axios.put(`/api/admin/books/${id}`, { ...meta, tags: (meta.tags || '').split(',').map(tag => tag.trim()).filter(Boolean) }, { headers: authHeaders() });
      setBook(data);
      ping('Saved');
    } catch (e) {
      setErr(e?.response?.data?.message || 'Save failed');
    } finally {
      setSavingMeta(false);
    }
  };

  const changePublication = async () => {
    const status = book.publicationStatus === 'draft' ? 'published' : 'draft';
    if (status === 'draft' && !window.confirm('Move this book back to draft? Readers will no longer find or open it.')) return;
    setSavingPublication(true);
    setErr('');
    try {
      const { data } = await axios.put(`/api/admin/books/${id}/publication`, { status }, { headers: authHeaders() });
      setBook(data);
      ping(status === 'published' ? 'Book published' : 'Book moved to draft');
    } catch (e) {
      setErr(e?.response?.data?.message || (e?.response ? `Could not change publication status (blocked by the server or security filter, HTTP ${e.response.status}). Tell support the book title and time.` : 'Could not change publication status. Check your connection and try again.'));
    } finally {
      setSavingPublication(false);
    }
  };

  if (err && !book) {
    return (
      <div className="admin-page">
        <header className="admin-page-head">
          <h1 className="admin-page-title">Book not found</h1>
          <p className="admin-page-sub">{err}</p>
        </header>
        <Link to="/admin/books" className="admin-btn">Back to books</Link>
      </div>
    );
  }
  if (!book) {
    return (
      <div className="admin-page">
        <div className="admin-loading"><div className="admin-spinner" /><p>Loading book…</p></div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-page-head" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="admin-page-title">{book.title || 'Untitled'}</h1>
          <p className="admin-page-sub">{book.author} · {book.pages?.length || 0} pages · {book.publicationStatus === 'draft' ? 'Draft' : 'Published'} · {book.hasQuiz ? 'quiz on' : 'no quiz'}</p>
        </div>
        <div style={{ display: 'inline-flex', gap: 8 }}>
          <Link to="/admin/books" className="admin-btn admin-btn-ghost">Back</Link>
          <button type="button" className={`admin-btn ${book.publicationStatus === 'draft' ? '' : 'admin-btn-ghost'}`} onClick={changePublication} disabled={savingPublication || savingMeta}>
            {savingPublication ? 'Updating…' : book.publicationStatus === 'draft' ? 'Publish book' : 'Move to draft'}
          </button>
          <Link to={`/read/${book._id}?adminPreview=1`} target="_blank" rel="noreferrer" className="admin-btn admin-btn-ghost">Preview as reader ↗</Link>
          {book.publicationStatus !== 'draft' && <Link to={`/read/${book._id}`} target="_blank" rel="noreferrer" className="admin-btn admin-btn-ghost">Open in reader ↗</Link>}
        </div>
      </header>

      <div className="admin-toolbar" style={{ marginBottom: 18 }}>
        <div className="admin-tabs" style={{ display: 'inline-flex', gap: 6, padding: 4, background: 'var(--admin-panel-bg)', border: '1px solid rgba(184,134,11,0.3)', borderRadius: 999 }}>
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              style={{
                padding: '6px 14px',
                border: 0,
                borderRadius: 999,
                cursor: 'pointer',
                background: tab === t.id ? 'linear-gradient(135deg, #b8860b, #daa520)' : 'transparent',
                color: tab === t.id ? '#1a1207' : 'var(--admin-fg-muted)',
                fontWeight: 700,
                fontSize: '0.78rem',
                letterSpacing: 0.5,
                textTransform: 'uppercase',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {err && <div className="admin-toast admin-toast-error">{err}</div>}
      {toast && <div className="admin-toast">{toast}</div>}

      {tab === 'meta' && (
        <div className="admin-card">
          <h2>Details</h2>
          <div className="admin-row">
            {META_FIELDS.map((f) => {
              const isCover = f.key === 'coverImage' || f.key === 'coverImageAr';
              const isCategory = f.key === 'category';
              const isGenre = f.type === 'genre';
              // Build the union of (admin-defined options) + (current values
              // already on this book), so a book whose stored value isn't in
              // the catalogue yet still shows it checked.
              const catOptions = (() => {
                if (!isCategory) return null;
                const names = new Set(categories.map((c) => c.name).filter(Boolean));
                (meta.categories || []).forEach((c) => { if (c) names.add(c); });
                return Array.from(names).sort((a, b) => a.localeCompare(b));
              })();
              const genreOptions = (() => {
                if (!isGenre) return null;
                const names = new Set(genres.map((g) => g.name).filter(Boolean));
                (meta.genres || []).forEach((g) => { if (g) names.add(g); });
                return Array.from(names).sort((a, b) => a.localeCompare(b));
              })();
              const toggleInArray = (field, name) => setMeta((m) => {
                const cur = Array.isArray(m[field]) ? m[field] : [];
                const i = cur.findIndex((x) => x.toLowerCase() === name.toLowerCase());
                const next = i >= 0 ? cur.filter((_, ix) => ix !== i) : [...cur, name];
                return { ...m, [field]: next };
              });
              const isChecked = (arr, name) =>
                Array.isArray(arr) && arr.some((x) => x.toLowerCase() === name.toLowerCase());
              return (
                <div className="admin-field" key={f.key}>
                  <label className="admin-label">{f.label}</label>
                  {isCategory ? (
                    <>
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: 8,
                        padding: 10, borderRadius: 10,
                        border: '1px solid rgba(184,134,11,0.3)',
                        background: 'rgba(184,134,11,0.04)',
                        maxHeight: 220, overflowY: 'auto',
                      }}>
                        {catOptions.length === 0 && (
                          <span style={{ fontSize: '0.78rem', color: 'var(--admin-fg-faint)' }}>
                            No categories yet — add one in Manage categories.
                          </span>
                        )}
                        {catOptions.map((name) => {
                          const on = isChecked(meta.categories, name);
                          return (
                            <label key={name} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              padding: '4px 10px', borderRadius: 999,
                              border: on ? '1px solid var(--admin-accent, #f0c560)' : '1px solid rgba(184,134,11,0.3)',
                              background: on ? 'rgba(240,197,96,0.12)' : 'transparent',
                              cursor: 'pointer', fontSize: '0.82rem',
                            }}>
                              <input
                                type="checkbox"
                                checked={on}
                                onChange={() => toggleInArray('categories', name)}
                                style={{ margin: 0 }}
                              />
                              {name}
                            </label>
                          );
                        })}
                      </div>
                      <span style={{ fontSize: '0.72rem', color: 'var(--admin-fg-faint)', marginTop: 4 }}>
                        Tick all that apply. Save details before leaving. <Link to={`/admin/categories?return=${encodeURIComponent(`/admin/books/${id}`)}`} style={{ color: 'var(--admin-accent, #f0c560)' }}>Manage categories</Link>
                      </span>
                    </>
                  ) : isGenre ? (
                    <>
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: 8,
                        padding: 10, borderRadius: 10,
                        border: '1px solid rgba(184,134,11,0.3)',
                        background: 'rgba(184,134,11,0.04)',
                        maxHeight: 220, overflowY: 'auto',
                      }}>
                        {genreOptions.length === 0 && (
                          <span style={{ fontSize: '0.78rem', color: 'var(--admin-fg-faint)' }}>
                            No genres yet — add one in Manage genres.
                          </span>
                        )}
                        {genreOptions.map((name) => {
                          const on = isChecked(meta.genres, name);
                          return (
                            <label key={name} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              padding: '4px 10px', borderRadius: 999,
                              border: on ? '1px solid var(--admin-accent, #f0c560)' : '1px solid rgba(184,134,11,0.3)',
                              background: on ? 'rgba(240,197,96,0.12)' : 'transparent',
                              cursor: 'pointer', fontSize: '0.82rem',
                            }}>
                              <input
                                type="checkbox"
                                checked={on}
                                onChange={() => toggleInArray('genres', name)}
                                style={{ margin: 0 }}
                              />
                              {name}
                            </label>
                          );
                        })}
                      </div>
                      <span style={{ fontSize: '0.72rem', color: 'var(--admin-fg-faint)', marginTop: 4 }}>
                        Tick all that apply. Save details before leaving. <Link to={`/admin/genres?return=${encodeURIComponent(`/admin/books/${id}`)}`} style={{ color: 'var(--admin-accent, #f0c560)' }}>Manage genres</Link>
                      </span>
                    </>
                  ) : f.type === 'select' ? (
                    <select
                      className="admin-select"
                      value={meta[f.key] || ''}
                      onChange={(e) => setMeta((m) => ({ ...m, [f.key]: e.target.value }))}
                    >
                      {f.options.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
                    </select>
                  ) : isCover ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <input
                        className="admin-input"
                        type="text"
                        value={meta[f.key] || ''}
                        onChange={(e) => setMeta((m) => ({ ...m, [f.key]: e.target.value }))}
                      />
                      <FileUploader
                        slug={metaSlug(book, meta)}
                        label="Upload"
                        accept="image/*,.jfif,.avif"
                        onUploaded={(url) => setMeta((m) => ({ ...m, [f.key]: url }))}
                      />
                      {meta[f.key] && (
                        <button
                          type="button"
                          className="admin-btn admin-btn-ghost admin-btn-sm"
                          title="Clear cover image URL"
                          onClick={() => setMeta((m) => ({ ...m, [f.key]: '' }))}
                        >Clear</button>
                      )}
                    </div>
                  ) : (
                    <input
                      className="admin-input"
                      type="text"
                      value={meta[f.key] || ''}
                      onChange={(e) => setMeta((m) => ({ ...m, [f.key]: e.target.value }))}
                    />
                  )}
                  {isCover && meta[f.key] && (
                    <img src={meta[f.key]} alt="" style={{ marginTop: 6, height: 80, width: 'auto', borderRadius: 6, objectFit: 'cover', boxShadow: '0 4px 10px rgba(0,0,0,0.4)' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  )}
                </div>
              );
            })}
          </div>
          <label className="admin-field">
            <span className="admin-label">Search tags</span>
            <input className="admin-input" type="text" value={meta.tags || ''} onChange={e => setMeta(m => ({ ...m, tags: e.target.value }))} placeholder="e.g. friendship, courage, animals" />
            <span style={{ fontSize: '0.72rem', color: 'var(--admin-fg-faint)' }}>Separate keywords with commas. Tags help readers search; categories and genres control browsing.</span>
          </label>
          <div className="admin-field">
            <label className="admin-label">Description (EN)</label>
            <textarea
              className="admin-textarea"
              rows={3}
              value={meta.description || ''}
              onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))}
            />
          </div>
          <div className="admin-field">
            <label className="admin-label">Description (AR)</label>
            <textarea
              className="admin-textarea"
              rows={3}
              dir="rtl"
              value={meta.descriptionAr || ''}
              onChange={(e) => setMeta((m) => ({ ...m, descriptionAr: e.target.value }))}
            />
          </div>
          <div className="admin-field">
            <label className="admin-label">Book format<select className="admin-input" value={meta.format || 'portrait'} onChange={e => setMeta(m => ({ ...m, format: e.target.value }))}><option value="portrait">Portrait</option><option value="landscape">Landscape</option></select></label>
            <label className="admin-label">Page background</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {PAGE_BACKGROUNDS.map((b) => {
                const selected = (meta.pageBackground || 'cream') === b.value;
                return (
                  <button
                    type="button"
                    key={b.value}
                    onClick={() => setMeta((m) => ({ ...m, pageBackground: b.value }))}
                    title={b.label}
                    style={{
                      display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      padding: 6, borderRadius: 8,
                      background: 'transparent',
                      border: selected ? '2px solid var(--admin-accent, #f0c560)' : '2px solid transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{
                      width: 44, height: 44, borderRadius: 8,
                      background: b.css,
                      border: '1px solid rgba(184,134,11,0.4)',
                      boxShadow: selected ? '0 0 0 2px rgba(240,197,96,0.25)' : 'none',
                    }} />
                    <span style={{ fontSize: '0.72rem', color: 'var(--admin-fg-muted)' }}>{b.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="admin-field">
            <label className="admin-label">Page layout</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {PAGE_LAYOUTS.map((l) => {
                const selected = (meta.pageLayout || 'image-text') === l.value;
                return (
                  <label
                    key={l.value}
                    style={{
                      display: 'flex', gap: 10, padding: 10, borderRadius: 8,
                      border: selected ? '2px solid var(--admin-accent, #f0c560)' : '1px solid rgba(184,134,11,0.3)',
                      background: selected ? 'rgba(240,197,96,0.06)' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="pageLayout"
                      value={l.value}
                      checked={selected}
                      onChange={() => setMeta((m) => ({ ...m, pageLayout: l.value }))}
                      style={{ marginTop: 3 }}
                    />
                    <span>
                      <span style={{ fontWeight: 700 }}>{l.label}</span>
                      <span style={{ display: 'block', fontSize: '0.78rem', color: 'var(--admin-fg-muted)', marginTop: 2 }}>{l.help}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="admin-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <input
              type="checkbox"
              id="hasQuiz"
              checked={!!meta.hasQuiz}
              onChange={(e) => setMeta((m) => ({ ...m, hasQuiz: e.target.checked }))}
            />
            <label htmlFor="hasQuiz" className="admin-label" style={{ marginBottom: 0 }}>Has quiz</label>
          </div>
          <div className="admin-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <input
              type="checkbox"
              id="ratingsVisible"
              checked={meta.ratingsVisible !== false}
              onChange={(e) => setMeta((m) => ({ ...m, ratingsVisible: e.target.checked }))}
            />
            <label htmlFor="ratingsVisible" className="admin-label" style={{ marginBottom: 0 }}>
              Show ratings publicly
              <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--admin-fg-muted)', fontWeight: 400, marginTop: 2 }}>
                Average + vote count shown on book cards and the details popup. Kids can still rate this book at end-of-book either way.
              </span>
            </label>
          </div>
          <button type="button" className="admin-btn" onClick={saveMeta} disabled={savingMeta}>
            {savingMeta ? 'Saving…' : 'Save details'}
          </button>
        </div>
      )}

      {tab === 'pages' && <PagesEditor book={book} setBook={setBook} setErr={setErr} ping={ping} />}
      {tab === 'quiz'  && <QuizEditor   book={book} setBook={setBook} setErr={setErr} ping={ping} />}
    </div>
  );
}

/* Small file uploader that posts to /api/admin/upload/:slug and writes the
   returned URL into the given book-page field. */
function FileUploader({ slug, label, accept, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`/api/admin/upload/${encodeURIComponent(slug || 'misc')}`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      onUploaded?.(data.url, data.originalName || file.name);
    } catch (err) {
      alert(err?.response?.data?.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="admin-btn admin-btn-ghost admin-btn-sm" style={{ cursor: 'pointer' }}>
      {busy ? 'Uploading…' : (label || 'Upload')}
      <input type="file" accept={accept} onChange={onPick} disabled={busy} style={{ display: 'none' }} />
    </label>
  );
}

/* ────────────────────────── Pages ────────────────────────── */
function PagesEditor({ book, setBook, setErr, ping }) {
  // Derive a slug from the existing coverImage path so uploads land in the same folder
  const slug = useMemo(() => {
    const m = (book.coverImage || '').match(/\/uploads\/books\/([^/]+)\//);
    return m ? m[1] : (book.title || 'misc').toLowerCase().replace(/[^a-z0-9_-]+/g, '-');
  }, [book.coverImage, book.title]);

  // Per-page alignment state: { [pageNumber]: { en?: 'running'|'done'|'error: …', ar?: ... } }
  const [aligning, setAligning] = useState({});
  const [alignBusy, setAlignBusy] = useState(false);
  const alignBusyRef = useRef(false);
  const setAlignStatus = (pn, lang, status) => {
    setAligning((s) => ({ ...s, [pn]: { ...(s[pn] || {}), [lang]: status } }));
  };
  const sorted = useMemo(() => (book.pages || []).slice().sort((a, b) => a.pageNumber - b.pageNumber), [book.pages]);
  const [working, setWorking] = useState({}); // pageNumber -> draft

  const draft = (p) => working[p.pageNumber] ?? p;

  const onChange = (p, key, val) => {
    setWorking((w) => ({ ...w, [p.pageNumber]: { ...(w[p.pageNumber] || p), [key]: val } }));
  };
  const onChangeMany = (p, patch) => setWorking(w => ({ ...w, [p.pageNumber]: { ...(w[p.pageNumber] || p), ...patch } }));

  // Escape plain page text into safe HTML so legacy pages (no htmlEn/Ar
  // saved yet) still seed the rich editor with their existing content.
  const escapeForHtml = (s) => String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');

  const alignPage = async (p, lang) => {
    if (alignBusyRef.current) {
      setAlignStatus(p.pageNumber, lang, 'error: another alignment is still running');
      return false;
    }
    alignBusyRef.current = true;
    setAlignBusy(true);
    setAlignStatus(p.pageNumber, lang, 'running');
    try {
      const { data } = await axios.post(
        `/api/admin/books/${book._id}/pages/${p.pageNumber}/align?lang=${lang}`,
        null,
        { headers: authHeaders(), timeout: 6 * 60 * 1000 }
      );
      setAlignStatus(p.pageNumber, lang, `done (${data.words || 0} words)`);
      return true;
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Alignment failed';
      setAlignStatus(p.pageNumber, lang, `error: ${msg}`);
      return false;
    } finally {
      alignBusyRef.current = false;
      setAlignBusy(false);
    }
  };

  const savePage = async (p) => {
    const d = draft(p);
    try {
      const payload = {
        // textEn/textAr are derived server-side from htmlEn/htmlAr (so the
        // aligner stays in sync); we still send them for legacy callers
        // that haven't moved to the rich editor.
        textEn: d.textEn || '',
        textAr: d.textAr || '',
        image: d.image || '',
        audioEn: d.audioEn || '',
        audioAr: d.audioAr || '',
        audioEnName: d.audioEnName || '',
        audioArName: d.audioArName || '',
      };
      // Empty rich-text fields on legacy pages must not overwrite their
      // existing plain text when an unrelated image or audio field is saved.
      if (d.htmlEn || (d.htmlEn || '') !== (p.htmlEn || '')) payload.htmlEn = d.htmlEn || '';
      if (d.htmlAr || (d.htmlAr || '') !== (p.htmlAr || '')) payload.htmlAr = d.htmlAr || '';
      const { data } = await axios.put(`/api/admin/books/${book._id}/pages/${p.pageNumber}`, payload, { headers: authHeaders() });
      const newPages = book.pages.map((x) => x.pageNumber === p.pageNumber ? { ...x, ...data } : x);
      if (!newPages.find((x) => x.pageNumber === p.pageNumber)) newPages.push({ ...data });
      newPages.sort((a, b) => a.pageNumber - b.pageNumber);
      setBook({ ...book, pages: newPages });
      setWorking((w) => { const c = { ...w }; delete c[p.pageNumber]; return c; });
      ping(`Saved page ${p.pageNumber}`);

      // Formatting-only and image edits do not invalidate word timing.
      // Run changed languages one at a time because the server allows one media job.
      if (data.audioEn && (data.textEn || '').trim() && (p.audioEn !== data.audioEn || p.textEn !== data.textEn)) {
        await alignPage(p, 'en');
      }
      if (data.audioAr && (data.textAr || '').trim() && (p.audioAr !== data.audioAr || p.textAr !== data.textAr)) {
        await alignPage(p, 'ar');
      }
    } catch (e) {
      setErr(e?.response?.data?.message || 'Save failed');
    }
  };

  const removePage = async (p) => {
    if (!window.confirm(`Delete page ${p.pageNumber}?`)) return;
    try {
      await axios.delete(`/api/admin/books/${book._id}/pages/${p.pageNumber}`, { headers: authHeaders() });
      setBook({ ...book, pages: book.pages.filter((x) => x.pageNumber !== p.pageNumber) });
      ping(`Deleted page ${p.pageNumber}`);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Delete failed');
    }
  };

  const addPage = () => {
    const next = (sorted[sorted.length - 1]?.pageNumber ?? -1) + 1;
    setBook({ ...book, pages: [...book.pages, { pageNumber: next, textEn: '', textAr: '', image: '', audioEn: '', audioAr: '' }] });
  };

  return (
    <div className="admin-card">
      <h2>Pages ({sorted.length})</h2>
      {sorted.map((p) => {
        const d = draft(p);
        const dirty = JSON.stringify(d) !== JSON.stringify(p);
        const a = aligning[p.pageNumber] || {};
        return (
          <div key={p.pageNumber} style={{ borderTop: '1px solid rgba(184,134,11,0.18)', paddingTop: 16, marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
              <strong style={{ color: 'var(--admin-accent, #f0c560)', letterSpacing: 1.2 }}>PAGE {p.pageNumber}{p.pageNumber === 0 ? ' (cover)' : ''}</strong>
              <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {(d.audioEn && (d.textEn || '').trim()) ? (
                  <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" disabled={alignBusy} onClick={() => alignPage(p, 'en')}>
                    {a.en === 'running' ? '⏳ EN aligning…' : '↻ Re-align EN'}
                  </button>
                ) : null}
                {(d.audioAr && (d.textAr || '').trim()) ? (
                  <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" disabled={alignBusy} onClick={() => alignPage(p, 'ar')}>
                    {a.ar === 'running' ? '⏳ AR aligning…' : '↻ Re-align AR'}
                  </button>
                ) : null}
                <button type="button" className="admin-btn admin-btn-sm" disabled={!dirty || alignBusy} onClick={() => savePage(p)}>
                  {alignBusy ? 'Processing…' : 'Save'}
                </button>
                <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" disabled={alignBusy} onClick={() => removePage(p)}>Delete</button>
              </div>
            </div>
            {(a.en || a.ar) && (
              <div style={{
                display: 'flex', gap: 12, flexWrap: 'wrap',
                padding: '8px 12px', marginBottom: 10,
                background: 'var(--admin-panel-bg)', borderRadius: 8,
                fontSize: '0.82rem', color: 'var(--admin-fg)',
              }}>
                {a.en && (
                  <span>
                    EN aligner: {a.en === 'running'
                      ? <em style={{ color: 'var(--admin-accent, #f0c560)' }}>processing audio… please wait (~30–60s)</em>
                      : a.en.startsWith('error')
                        ? <span style={{ color: '#ffb19a' }}>{a.en}</span>
                        : <span style={{ color: '#b6f0d8' }}>✓ {a.en}</span>}
                  </span>
                )}
                {a.ar && (
                  <span>
                    AR aligner: {a.ar === 'running'
                      ? <em style={{ color: 'var(--admin-accent, #f0c560)' }}>processing audio… please wait (~30–60s)</em>
                      : a.ar.startsWith('error')
                        ? <span style={{ color: '#ffb19a' }}>{a.ar}</span>
                        : <span style={{ color: '#b6f0d8' }}>✓ {a.ar}</span>}
                  </span>
                )}
              </div>
            )}
            <div className="admin-row">
              <div className="admin-field">
                <label className="admin-label">Image URL</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="admin-input" value={d.image || ''} onChange={(e) => onChange(p, 'image', e.target.value)} />
                  <FileUploader slug={slug} label="Upload" accept="image/*,.jfif,.avif" onUploaded={(url) => onChange(p, 'image', url)} />
                  {d.image && (
                    <button
                      type="button"
                      className="admin-btn admin-btn-ghost admin-btn-sm"
                      title="Clear image URL"
                      onClick={() => onChange(p, 'image', '')}
                    >Clear</button>
                  )}
                </div>
              </div>
              <div className="admin-field">
                <label className="admin-label">Audio EN</label>
                {d.audioEnName && <span style={{ fontSize: '0.82rem', color: 'var(--admin-fg)' }}>Uploaded file: {d.audioEnName}</span>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="admin-input" aria-label="Audio EN URL" value={d.audioEn || ''} onChange={(e) => onChangeMany(p, { audioEn: e.target.value, audioEnName: '' })} />
                  <FileUploader slug={slug} label="Upload" accept="audio/*" onUploaded={(url, name) => onChangeMany(p, { audioEn: url, audioEnName: name })} />
                  {d.audioEn && (
                    <button
                      type="button"
                      className="admin-btn admin-btn-ghost admin-btn-sm"
                      title="Clear EN audio URL"
                      onClick={() => onChangeMany(p, { audioEn: '', audioEnName: '' })}
                    >Clear</button>
                  )}
                </div>
              </div>
              <div className="admin-field">
                <label className="admin-label">Audio AR</label>
                {d.audioArName && <span style={{ fontSize: '0.82rem', color: 'var(--admin-fg)' }}>Uploaded file: {d.audioArName}</span>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="admin-input" aria-label="Audio AR URL" value={d.audioAr || ''} onChange={(e) => onChangeMany(p, { audioAr: e.target.value, audioArName: '' })} />
                  <FileUploader slug={slug} label="Upload" accept="audio/*" onUploaded={(url, name) => onChangeMany(p, { audioAr: url, audioArName: name })} />
                  {d.audioAr && (
                    <button
                      type="button"
                      className="admin-btn admin-btn-ghost admin-btn-sm"
                      title="Clear AR audio URL"
                      onClick={() => onChangeMany(p, { audioAr: '', audioArName: '' })}
                    >Clear</button>
                  )}
                </div>
              </div>
            </div>
            <div className="admin-field">
              <label className="admin-label">Text (EN)</label>
              <RichTextEditor
                value={d.htmlEn || escapeForHtml(d.textEn)}
                onChange={(html) => onChangeMany(p, { htmlEn: html, ...(html ? {} : { textEn: '' }) })}
                slug={slug}
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Text (AR)</label>
              <RichTextEditor
                value={d.htmlAr || escapeForHtml(d.textAr)}
                onChange={(html) => onChangeMany(p, { htmlAr: html, ...(html ? {} : { textAr: '' }) })}
                dir="rtl"
                slug={slug}
              />
            </div>
          </div>
        );
      })}
      <div style={{ marginTop: 18 }}>
        <button type="button" className="admin-btn admin-btn-ghost" onClick={addPage}>+ Add page</button>
      </div>
    </div>
  );
}

/* ────────────────────────── Quiz ────────────────────────── */
function QuizEditor({ book, setBook, setErr, ping }) {
  const [lang, setLang] = useState('en');
  const list = book.quiz?.[lang] || [];
  const optionLetters = lang === 'ar' ? ['ا', 'ب', 'ت', 'ث'] : ['a', 'b', 'c', 'd'];

  const update = (idx, patch) => {
    const next = list.slice();
    next[idx] = { ...next[idx], ...patch };
    pushLang(next);
  };
  const updateOption = (idx, oi, patch) => {
    const next = list.slice();
    const opts = next[idx].options.slice();
    opts[oi] = { ...opts[oi], ...patch };
    next[idx] = { ...next[idx], options: opts };
    pushLang(next);
  };
  const addQ = () => {
    pushLang([...list, {
      prompt: '',
      options: optionLetters.map((k) => ({ key: k, text: '' })),
      correctKey: optionLetters[0],
    }]);
  };
  const removeQ = (idx) => {
    const next = list.slice();
    next.splice(idx, 1);
    pushLang(next);
  };

  const pushLang = (next) => {
    setBook({ ...book, quiz: { ...book.quiz, [lang]: next } });
  };

  const saveAll = async () => {
    setErr('');
    try {
      const { data } = await axios.put(`/api/admin/books/${book._id}/quiz`, {
        en: book.quiz?.en || [],
        ar: book.quiz?.ar || [],
        hasQuiz: !!(book.quiz?.en?.length || book.quiz?.ar?.length),
      }, { headers: authHeaders() });
      setBook({ ...book, quiz: data.quiz, hasQuiz: data.hasQuiz });
      ping('Quiz saved');
    } catch (e) {
      const appMessage = e?.response?.data?.message;
      setErr(appMessage || (e?.response?.status === 403
        ? 'The security filter blocked this quiz save. Your edits are still here. Try once more; if it continues, tell support the book title and time.'
        : 'Save failed. Your quiz edits are still here; please try again.'));
    }
  };

  return (
    <div className="admin-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <h2 style={{ margin: 0 }}>Quiz · {list.length} questions</h2>
        <div style={{ display: 'inline-flex', gap: 6 }}>
          <button type="button" className={`admin-btn admin-btn-sm ${lang === 'en' ? '' : 'admin-btn-ghost'}`} onClick={() => setLang('en')}>EN</button>
          <button type="button" className={`admin-btn admin-btn-sm ${lang === 'ar' ? '' : 'admin-btn-ghost'}`} onClick={() => setLang('ar')}>ع</button>
        </div>
      </div>

      {list.map((q, i) => (
        <div key={i} style={{ borderTop: '1px solid rgba(184,134,11,0.18)', paddingTop: 14, marginTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong style={{ color: 'var(--admin-accent, #f0c560)', letterSpacing: 1.2 }}>Q{i + 1}</strong>
            <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => removeQ(i)}>Remove</button>
          </div>
          <div className="admin-field">
            <label className="admin-label">Prompt</label>
            <textarea
              className="admin-textarea"
              rows={2}
              dir={lang === 'ar' ? 'rtl' : 'ltr'}
              value={q.prompt || ''}
              onChange={(e) => update(i, { prompt: e.target.value })}
            />
          </div>
          <div className="admin-row">
            {(q.options || []).map((opt, oi) => (
              <div key={oi} className="admin-field">
                <label className="admin-label">Option {opt.key}</label>
                <input
                  className="admin-input"
                  dir={lang === 'ar' ? 'rtl' : 'ltr'}
                  value={opt.text || ''}
                  onChange={(e) => updateOption(i, oi, { text: e.target.value })}
                />
              </div>
            ))}
          </div>
          <div className="admin-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <label className="admin-label" style={{ marginBottom: 0 }}>Correct answer</label>
            <select
              className="admin-select"
              style={{ maxWidth: 120 }}
              value={q.correctKey || optionLetters[0]}
              onChange={(e) => update(i, { correctKey: e.target.value })}
            >
              {optionLetters.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
        </div>
      ))}

      <div style={{ marginTop: 18, display: 'inline-flex', gap: 8 }}>
        <button type="button" className="admin-btn admin-btn-ghost" onClick={addQ}>+ Add question</button>
        <button type="button" className="admin-btn" onClick={saveAll}>Save all</button>
      </div>
    </div>
  );
}
